'use client';

import ReactECharts from 'echarts-for-react';
import {useMediaQuery} from 'common/hooks/is_mobile';
import type {TopicWeight} from 'iq-questions/types';

interface Props {
  visible: Array<TopicWeight>;
  showAll: boolean;
  // Canonical topic order used to pin a stable color per topic across filter changes.
  // Falls back to positional coloring when omitted.
  colorOrder?: Array<string>;
}

function barColor(name: string, showAll: boolean, paletteIndex: number): string {
  if (name === 'Others' && !showAll) return '#CBD5E1';
  return BAR_COLORS[paletteIndex % BAR_COLORS.length]!;
}

const BAR_COLORS = [
  '#F59E0B',
  '#3B82F6',
  '#10B981',
  '#8B5CF6',
  '#EF4444',
  '#06B6D4',
  '#F97316',
  '#EC4899',
  '#84CC16',
  '#6366F1',
  '#14B8A6',
  '#A855F7',
  '#EAB308',
];

const BAR_HEIGHT = 36;
const MIN_BAR_PX = 45;

// Break a topic name into two balanced lines on the nearest word boundary, so narrow
// screens don't hand the whole row width to the label and starve the bars.
function twoLineLabel(name: string): string {
  const words = name.split(' ');
  if (words.length < 2) return name;
  let split = 1;
  let smallestDiff = Infinity;
  for (let i = 1; i < words.length; i++) {
    const diff = Math.abs(
      words.slice(0, i).join(' ').length - words.slice(i).join(' ').length,
    );
    if (diff < smallestDiff) {
      smallestDiff = diff;
      split = i;
    }
  }
  return `${words.slice(0, split).join(' ')}\n${words.slice(split).join(' ')}`;
}

export function TopicBarChart({visible, showAll, colorOrder}: Props) {
  const wrapLabels = useMediaQuery('(max-width: 449px)');
  const names = visible.map((t) => t.topic);
  const maxWeight = Math.max(...visible.map((t) => t.weight));

  const option = {
    grid: {left: 8, right: 64, top: 8, bottom: 8, containLabel: true},
    xAxis: {type: 'value', show: false, max: maxWeight},
    yAxis: {
      type: 'category',
      data: names,
      inverse: true,
      axisLine: {show: false},
      axisTick: {show: false},
      axisLabel: {
        color: '#1a1a1a',
        fontSize: 12,
        fontFamily: 'Overpass, sans-serif',
        lineHeight: 14,
        ...(wrapLabels ? {formatter: twoLineLabel} : {}),
      },
    },
    series: [
      {
        type: 'bar',
        silent: true,
        data: visible.map((topic, i) => {
          const orderIndex = colorOrder?.indexOf(topic.topic) ?? -1;
          const paletteIndex = orderIndex >= 0 ? orderIndex : i;
          return {
            value: topic.weight,
            itemStyle: {color: barColor(topic.topic, showAll, paletteIndex), borderRadius: [0, 4, 4, 0]},
          };
        }),
        barMaxWidth: 28,
        minBarWidth: MIN_BAR_PX,
        label: {
          show: true,
          position: 'right',
          formatter: (params: {value: number}) => `${(params.value * 100).toFixed(1)}%`,
          color: '#6B7280',
          fontFamily: 'Overpass, sans-serif',
          fontSize: 12,
          fontWeight: 600,
        },
      },
    ],
    animation: true,
    animationDuration: 1500,
    animationEasing: 'quadraticInOut',
    animationDelay: (idx: number) => idx * 60,
  };

  return (
    <ReactECharts
      option={option}
      style={{height: visible.length * BAR_HEIGHT, width: '100%'}}
      opts={{renderer: 'canvas'}}
    />
  );
}
